// Spinner — a small spinning ring used inside buttons and to fill loading
// modals. Inherits `currentColor`, so it takes the text color of wherever
// it's placed. Honors prefers-reduced-motion (falls back to a static ring).
import { cn } from "@/lib/cn";

const SIZES = { sm: "h-3.5 w-3.5", md: "h-4 w-4", lg: "h-6 w-6" } as const;

export function Spinner({
  size = "md",
  className,
}: {
  size?: keyof typeof SIZES;
  className?: string;
}) {
  return (
    <svg
      className={cn(SIZES[size], "animate-spin motion-reduce:animate-none", className)}
      viewBox="0 0 24 24"
      fill="none"
      role="status"
      aria-label="Loading"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-90"
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Centered spinner + optional label — for filling a modal body (or any
 *  block) while its content loads. Fades in so a fast load doesn't flash. */
export function Loading({
  label = "Loading",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex animate-in fade-in-0 flex-col items-center justify-center gap-3 py-12 text-text-secondary",
        className,
      )}
    >
      <Spinner size="lg" className="text-accent" />
      <p className="text-xs">{label}…</p>
    </div>
  );
}
